import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Spin, Typography} from 'antd';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {fullyDecodeURL} from 'utils/helper';
import {isLikelyHttpUrl, parseS3Url} from '../kycComplyCubeUtils';
import styles from '../kycDashboard.module.scss';

const {Text} = Typography;

export function Thumb({src, alt}) {
  const [broken, setBroken] = useState(false);
  if (!src || !isLikelyHttpUrl(src) || broken) {
    return <div className={styles.thumbPlaceholder}>No preview</div>;
  }
  return (
    <img
      src={src}
      alt={alt || ''}
      className={styles.thumb}
      onError={() => setBroken(true)}
    />
  );
}

/**
 * ComplyCube media lives in private S3 buckets, so a raw S3 URL can't be
 * displayed directly. Presign it (same flow as RegenerateAWSURL) before use.
 * Non-S3 http URLs are passed through as-is.
 */
export function useSignedMediaUrl(rawUrl) {
  const [presignMediaURL] = useMutation(PRE_SIGN_MEDIA_URL);
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const parsed = parseS3Url(rawUrl);
    if (!parsed) {
      setSignedUrl(isLikelyHttpUrl(rawUrl) ? rawUrl : null);
      return undefined;
    }
    setLoading(true);
    presignMediaURL({
      variables: {
        input: {
          filename: fullyDecodeURL(parsed.filename),
          bucket: parsed.bucket,
          upload: false,
        },
      },
    })
      .then(({data}) => {
        if (cancelled) return;
        const res = data?.presignMediaURL;
        setSignedUrl(
          res?.__typename === 'Error' ? null : res?.data?.url || null,
        );
      })
      .catch(() => {
        if (!cancelled) setSignedUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rawUrl, presignMediaURL]);

  return {signedUrl, loading};
}

export function SignedThumbLink({rawUrl, alt, caption}) {
  const {signedUrl, loading} = useSignedMediaUrl(rawUrl);
  return (
    <a
      href={signedUrl || undefined}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.thumbLink}>
      {loading ? (
        <div className={styles.thumbPlaceholder}>
          <Spin size="small" />
        </div>
      ) : (
        <Thumb src={signedUrl} alt={alt} />
      )}
      <Text type="secondary" className={styles.thumbCaption}>
        {caption}
      </Text>
    </a>
  );
}
